import type { EmbedWidgetConfig } from './default-widgets';

export type EmbedConfig = {
  accessToken: string;
  widgets?: EmbedWidgetConfig[];
  colors?: { primary?: string };
  theme?: 'light' | 'dark';
  /** Legacy flag (pre-`theme`): `false` forced light mode. */
  enableDarkMode?: boolean;
  height?: string;
  elementId?: string;
};

// Tiny external store (read via useSyncExternalStore) so the app can boot in
// "simple mode" (?token= in the URL) and still accept the loader-posted full
// config ("advanced mode") without re-mounting React.
let config: EmbedConfig | null = null;
const listeners = new Set<() => void>();

export function getEmbedConfig(): EmbedConfig | null {
  return config;
}

export function subscribeEmbedConfig(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function resolveTheme(next: EmbedConfig): 'light' | 'dark' {
  if (next.theme) {
    return next.theme;
  }
  // Legacy contract: enableDarkMode === false forces light mode.
  if (next.enableDarkMode === false) {
    return 'light';
  }
  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  ) {
    return 'dark';
  }
  return 'light';
}

function applyTheme(next: EmbedConfig) {
  const root = window.document.documentElement;
  const theme = resolveTheme(next);
  root.classList.remove('light', 'dark');
  root.classList.add(theme);
  // Keep color-scheme in sync with the resolved theme (the index.html inline
  // script sets it pre-paint; this keeps it correct if init changes the theme).
  root.style.colorScheme = theme;
  if (next.colors?.primary) {
    root.style.setProperty('--primary', next.colors.primary);
  }
}

export function setEmbedConfig(next: EmbedConfig) {
  config = next;
  applyTheme(next);
  for (const listener of listeners) {
    listener();
  }
}

type IncomingMessage = {
  type?: string;
  data?: EmbedConfig;
};

/**
 * Boots the config store:
 * - simple mode: `/?token=<OTT JWT>` renders immediately with defaults
 * - advanced mode: announces readiness; the `/d.js` loader replies with an
 *   `init` message carrying the full config (widgets, colors, theme, ...)
 */
export function initEmbedConfig() {
  window.addEventListener('message', (event: MessageEvent<IncomingMessage>) => {
    const message = event.data;
    if (
      message &&
      typeof message === 'object' &&
      message.type === 'init' &&
      message.data &&
      typeof message.data.accessToken === 'string'
    ) {
      setEmbedConfig(message.data);
    }
  });

  const token = new URLSearchParams(window.location.search).get('token');
  if (token) {
    setEmbedConfig({ accessToken: token });
  }

  if (window.parent && window.parent !== window) {
    window.parent.postMessage({ type: 'ready' }, '*');
  }
}
